import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MessageCircle, Save, Eye, EyeOff, CheckCircle, AlertCircle, Send } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const WhatsAppSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showKeys, setShowKeys] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  const [testPhone, setTestPhone] = useState('');
  const [settings, setSettings] = useState({
    whatsapp_provider: 'twilio',
    // Twilio WhatsApp
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_whatsapp_number: '',
    // Meta/Facebook WhatsApp Business API
    meta_phone_number_id: '',
    meta_access_token: '',
    meta_business_account_id: '',
    // WATI
    wati_api_endpoint: '',
    wati_api_token: '',
    // Interakt
    interakt_api_key: '',
    interakt_phone_number: '',
    // 2Chat
    twochat_api_key: '',
    twochat_phone_number: '',
    // AiSensy
    aisensy_api_key: '',
    aisensy_partner_id: '',
    // Common
    is_enabled: false,
    enable_fee_reminder: true,
    enable_admission_notification: true,
    enable_attendance_alert: true,
    enable_exam_notification: true
  });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/settings/whatsapp');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await apiClient.post('/api/settings/whatsapp', settings);
      setMessage({ type: 'success', text: 'WhatsApp settings saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestWhatsApp = async () => {
    if (!testPhone) {
      setMessage({ type: 'error', text: 'Please enter a phone number to test' });
      return;
    }
    setTesting(true);
    setMessage({ type: '', text: '' });
    try {
      await apiClient.post('/api/settings/whatsapp/test', { phone: testPhone });
      setMessage({ type: 'success', text: 'Test WhatsApp message sent successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send test message. Check your configuration.' });
    } finally {
      setTesting(false);
    }
  };

  const toggleShowKey = (key) => {
    setShowKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const renderKeyInput = (label, name, placeholder) => (
    <div>
      <Label>{label}</Label>
      <div className="relative">
        <Input
          type={showKeys[name] ? 'text' : 'password'}
          value={settings[name] || ''}
          onChange={(e) => setSettings({ ...settings, [name]: e.target.value })}
          placeholder={placeholder}
          className="pr-10"
        />
        <button
          type="button"
          onClick={() => toggleShowKey(name)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {showKeys[name] ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">WhatsApp API Settings</h1>
        <p className="text-gray-600 mt-1">Configure WhatsApp Business API for notifications</p>
      </div>

      {message.text && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {message.text}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-green-600" />
            WhatsApp Provider Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label>WhatsApp Provider</Label>
            <Select value={settings.whatsapp_provider} onValueChange={(v) => setSettings({ ...settings, whatsapp_provider: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select Provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="meta">Meta (Official WhatsApp Business API)</SelectItem>
                <SelectItem value="twilio">Twilio WhatsApp</SelectItem>
                <SelectItem value="wati">WATI</SelectItem>
                <SelectItem value="interakt">Interakt</SelectItem>
                <SelectItem value="aisensy">AiSensy</SelectItem>
                <SelectItem value="twochat">2Chat</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Meta/Facebook WhatsApp Business API */}
          {settings.whatsapp_provider === 'meta' && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
              <h3 className="font-semibold text-blue-900">Meta WhatsApp Business API</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderKeyInput('Phone Number ID', 'meta_phone_number_id', 'Your Phone Number ID')}
                {renderKeyInput('Access Token', 'meta_access_token', 'Your Permanent Access Token')}
              </div>
              {renderKeyInput('Business Account ID', 'meta_business_account_id', 'Your Business Account ID')}
              <p className="text-xs text-blue-700">Get credentials from <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="underline">Meta Developer Portal</a></p>
            </div>
          )}

          {/* Twilio WhatsApp */}
          {settings.whatsapp_provider === 'twilio' && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-200 space-y-4">
              <h3 className="font-semibold text-red-900">Twilio WhatsApp Configuration</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderKeyInput('Account SID', 'twilio_account_sid', 'ACxxxxx')}
                {renderKeyInput('Auth Token', 'twilio_auth_token', 'Your Auth Token')}
              </div>
              <div>
                <Label>WhatsApp Number</Label>
                <Input
                  value={settings.twilio_whatsapp_number || ''}
                  onChange={(e) => setSettings({ ...settings, twilio_whatsapp_number: e.target.value })}
                  placeholder="whatsapp:+14155238886"
                />
              </div>
              <p className="text-xs text-red-700">Get credentials from <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="underline">Twilio Console</a></p>
            </div>
          )}

          {/* WATI */}
          {settings.whatsapp_provider === 'wati' && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-4">
              <h3 className="font-semibold text-green-900">WATI Configuration</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>API Endpoint</Label>
                  <Input
                    value={settings.wati_api_endpoint || ''}
                    onChange={(e) => setSettings({ ...settings, wati_api_endpoint: e.target.value })}
                    placeholder="https://live-server-xxxxx.wati.io"
                  />
                </div>
                {renderKeyInput('API Token', 'wati_api_token', 'Your WATI API Token')}
              </div>
              <p className="text-xs text-green-700">Get credentials from <a href="https://wati.io" target="_blank" rel="noopener noreferrer" className="underline">WATI Dashboard</a></p>
            </div>
          )}

          {/* Interakt */}
          {settings.whatsapp_provider === 'interakt' && (
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-4">
              <h3 className="font-semibold text-purple-900">Interakt Configuration</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderKeyInput('API Key', 'interakt_api_key', 'Your Interakt API Key')}
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    value={settings.interakt_phone_number || ''}
                    onChange={(e) => setSettings({ ...settings, interakt_phone_number: e.target.value })}
                    placeholder="+919876543210"
                  />
                </div>
              </div>
              <p className="text-xs text-purple-700">Get credentials from <a href="https://interakt.shop" target="_blank" rel="noopener noreferrer" className="underline">Interakt Dashboard</a></p>
            </div>
          )}

          {/* AiSensy */}
          {settings.whatsapp_provider === 'aisensy' && (
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 space-y-4">
              <h3 className="font-semibold text-orange-900">AiSensy Configuration</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderKeyInput('API Key', 'aisensy_api_key', 'Your AiSensy API Key')}
                <div>
                  <Label>Partner ID (Optional)</Label>
                  <Input
                    value={settings.aisensy_partner_id || ''}
                    onChange={(e) => setSettings({ ...settings, aisensy_partner_id: e.target.value })}
                    placeholder="Your Partner ID"
                  />
                </div>
              </div>
              <p className="text-xs text-orange-700">Get credentials from <a href="https://app.aisensy.com" target="_blank" rel="noopener noreferrer" className="underline">AiSensy Dashboard</a></p>
            </div>
          )}

          {/* 2Chat */}
          {settings.whatsapp_provider === 'twochat' && (
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 space-y-4">
              <h3 className="font-semibold text-yellow-900">2Chat Configuration</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderKeyInput('API Key', 'twochat_api_key', 'Your 2Chat API Key')}
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    value={settings.twochat_phone_number || ''}
                    onChange={(e) => setSettings({ ...settings, twochat_phone_number: e.target.value })}
                    placeholder="+919876543210"
                  />
                </div>
              </div>
              <p className="text-xs text-yellow-700">Get credentials from <a href="https://2chat.co" target="_blank" rel="noopener noreferrer" className="underline">2Chat Dashboard</a></p>
            </div>
          )}

          {/* Notification Options */}
          <div className="p-4 bg-gray-50 rounded-lg border space-y-3">
            <h3 className="font-semibold text-gray-800">Enable WhatsApp Notifications For:</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={settings.enable_fee_reminder} onChange={(e) => setSettings({ ...settings, enable_fee_reminder: e.target.checked })} className="rounded" />
                <span className="text-sm">Fee Reminders</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={settings.enable_admission_notification} onChange={(e) => setSettings({ ...settings, enable_admission_notification: e.target.checked })} className="rounded" />
                <span className="text-sm">Admission Notifications</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={settings.enable_attendance_alert} onChange={(e) => setSettings({ ...settings, enable_attendance_alert: e.target.checked })} className="rounded" />
                <span className="text-sm">Attendance Alerts</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={settings.enable_exam_notification} onChange={(e) => setSettings({ ...settings, enable_exam_notification: e.target.checked })} className="rounded" />
                <span className="text-sm">Exam Notifications</span>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg">
            <input
              type="checkbox"
              id="whatsapp_enabled"
              checked={settings.is_enabled}
              onChange={(e) => setSettings({ ...settings, is_enabled: e.target.checked })}
              className="rounded"
            />
            <Label htmlFor="whatsapp_enabled" className="cursor-pointer">Enable WhatsApp Service</Label>
          </div>

          <div className="flex gap-3">
            <Button onClick={handleSave} disabled={saving} className="flex-1">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save WhatsApp Settings'}
            </Button>
          </div>

          {/* Test WhatsApp */}
          <div className="p-4 border-t pt-4">
            <h3 className="font-semibold mb-3">Test WhatsApp Message</h3>
            <div className="flex gap-2">
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="Enter WhatsApp number (e.g., 919876543210)"
                className="flex-1"
              />
              <Button onClick={handleTestWhatsApp} disabled={testing} variant="outline">
                <Send className="w-4 h-4 mr-2" />
                {testing ? 'Sending...' : 'Send Test'}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">Include country code without + (e.g., 919876543210)</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WhatsAppSettings;
